'use client';

import { useState, useTransition } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { getMyQuizzes, makeDefaultQuiz } from '@/app/actions/quizzes';
import { QuizEditor } from '@/components/builder/QuizEditor';
import { tw } from '@tw';
import type { QuizTutorDto } from '@peach/types/quizzes/quiz.dto.js';
import type { SanitizedQuiz } from '@/lib/quiz/sanitize';

/**
 * The owner's doors under the quiz their page opens: post it, edit it here, or put another
 * of their quizzes on the page. A new quiz is written from a post, on /reply.
 */
export function OwnerQuizDoors({ quiz, username }: { quiz: SanitizedQuiz; username: string }) {
  const router = useRouter();
  const [open, setOpen] = useState<'edit' | 'pick' | null>(null);
  const [mine, setMine] = useState<QuizTutorDto[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();

  // The page carries the public quiz; the owner's own copy (answers, kit, source) is read on the first tap.
  const show = async (door: 'edit' | 'pick') => {
    setFailed(null);
    setOpen(door);
    if (mine) return;
    const result = await getMyQuizzes();
    if (result.success) setMine(result.data);
    else setFailed('Could not load your quizzes. Try again');
  };

  const pick = (id: string) =>
    startTransition(async () => {
      setFailed(null);
      const result = await makeDefaultQuiz(id, username);
      if (!result.success) return setFailed("That didn't save. Try again");
      setOpen(null);
      setMine(null);
      router.refresh();
    });

  const editing = open === 'edit' ? mine?.find((q) => q.id === quiz.id) : undefined;
  if (editing)
    return (
      <QuizEditor
        quiz={editing}
        owner={username}
        source="storefront"
        onClose={(saved) => {
          setOpen(null);
          if (!saved) return;
          setMine(null);
          router.refresh();
        }}
      />
    );

  return (
    <div className="flex flex-col gap-2">
      {quiz.hasKit && (
        <Link href={`/post/${quiz.slug}`} className="mm-btn sf-cta sf-cta-lime">
          Post quiz
        </Link>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void show('edit')}
          className="mm-btn sf-cta sf-cta-line flex-1"
        >
          Edit quiz
        </button>
        <button
          type="button"
          onClick={() => (open === 'pick' ? setOpen(null) : void show('pick'))}
          aria-expanded={open === 'pick'}
          className="mm-btn sf-cta sf-cta-line flex-1"
        >
          {open === 'pick' ? 'Close' : 'Change quiz'}
        </button>
      </div>
      {open && !failed && !mine && <p className="text-p-3 text-[var(--sf-mut)]">Loading</p>}
      {open === 'pick' && mine && (
        <section className="flex flex-col gap-1 rounded-[var(--sf-radius)] border border-[var(--sf-line)] bg-[var(--sf-card)] p-2">
          {mine.map((q) => (
            <button
              key={q.id}
              type="button"
              disabled={q.live || pending}
              onClick={() => pick(q.id)}
              className={tw(
                'flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left text-p-3',
                q.live ? 'bg-[var(--sf-ticket)]' : 'hover:bg-[var(--sf-ticket)] disabled:opacity-50'
              )}
            >
              <span className="min-w-0 truncate font-semibold text-[var(--sf-ink)]">{q.title}</span>
              {q.live && (
                <span className="shrink-0 text-badge font-semibold text-[var(--sf-mut)]">
                  On your page
                </span>
              )}
            </button>
          ))}
          <Link
            href="/reply"
            className="px-3 py-2.5 text-p-3 font-semibold text-[var(--sf-ink)] underline underline-offset-4"
          >
            Write a new one from a post
          </Link>
        </section>
      )}
      {failed && <p className="text-p-3 text-[var(--sf-mut)]">{failed}</p>}
      {/* Derived, so a list cached by Change quiz cannot leave the Edit door blank. */}
      {open === 'edit' && mine && !failed && (
        <p className="text-p-3 text-[var(--sf-mut)]">Could not load this quiz. Try again</p>
      )}
    </div>
  );
}
